import { ThemeProvider } from '@emotion/react'
import { Backdrop, CircularProgress, CssBaseline } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import React from 'react'
import { useEffect } from 'react'
import { Route, Routes } from 'react-router'
import { useRecoilState } from 'recoil'

import { PWAUpdater } from '../components/common'
import OnboardingDetector from '../components/onboarding/OnboardingDetector'
import PaymentDueBannerWrapper from '../components/subscription/PaymentDueBannerWrapper'
import { AnalyticsProvider } from '../contexts/AnalyticsContext'
import darkTheme from '../dark-theme'
import { usePageViewTracking } from '../hooks/usePageViewTracking'
import { getNewSession, sessionState } from '../state'
import theme from '../theme'
import { getPreviousOrCurrentSunday } from '../utils/calendarHelpers'
// import { checkAndCreateTestData } from '../utils/testData'

import AdminPage from './AdminPage'
import Book from './Book'
import CreateDeskPage from './CreateDeskPage'
import CreateOfficePage from './CreateOfficePage'
import EditDesks from './EditDesks'
import EditOfficePage from './EditOfficePage'
import ForgotPassword from './ForgotPassword'
import HelpPage from './Help'
import Home from './Home'
import Landing from './Landing'
import MyTeam from './MyTeam'
import OfficeNotifications from './OfficeNotifications'
import Offices from './Offices'
import OnboardingPage from './OnboardingPage'
import People from './People'
import Pricing from './Pricing'
import PrivacyPolicy from './PrivacyPolicy'
import Profile from './Profile'
import routes from './routes'
import Signin from './Signin'
import Signup from './Signup'
import TermsOfService from './TermsOfService'
import ViewOffice from './ViewOffice'

const App = () => {
  const [session, setSession] = useRecoilState(sessionState)
  const [init, setInit] = React.useState<boolean>(true)

  // Initialize test data for emulator (disabled)
  // useEffect(() => {
  //   checkAndCreateTestData()
  // }, [])

  useEffect(
    () =>
      onAuthStateChanged(getAuth(), async resultUser => {
        if (resultUser) {
          await getNewSession(
            setSession,
            resultUser?.uid ?? '',
            getPreviousOrCurrentSunday(new Date()),
            28,
            'Once off'
          )
        } else {
          // No user logged in - set isAuthenticating and isLoadingData to false
          setSession(old => ({
            ...old,
            isAuthenticating: false,
            isLoadingData: false,
            user: null
          }))
        }
        console.log('user state changes', resultUser)
        setInit(false)
      }),
    [setSession]
  )

  return (
    <ThemeProvider theme={session.user?.theme === 'light' ? theme : darkTheme}>
      <CssBaseline />
      {init ? (
        <Backdrop
          sx={{
            color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
            zIndex: theme => theme.zIndex.drawer + 1,
            backgroundColor: theme =>
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.95)'
                : 'rgba(255, 255, 255, 0.95)'
          }}
          open={true}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      ) : (
        <AnalyticsProvider>
          <AppContent />
        </AnalyticsProvider>
      )}
    </ThemeProvider>
  )
}

const AppContent = () => {
  // Track page views automatically on route changes
  usePageViewTracking()

  return (
    <PWAUpdater>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Payment Due Banner - appears at top of all authenticated pages */}
        <PaymentDueBannerWrapper />

        <Routes>
          {/* Public routes - no onboarding detection needed */}
          <Route path='/' element={<Landing />} />
          <Route path={routes.signin} element={<Signin />} />
          <Route path={routes.signup} element={<Signup />} />
          <Route path={routes.pricing} element={<Pricing />} />
          <Route path={routes.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={routes.termsOfService} element={<TermsOfService />} />
          <Route path={routes.help} element={<HelpPage />} />
          <Route
            path={routes.forgotPassword}
            element={<ForgotPassword />}
          />

          {/* Authenticated routes - wrapped with OnboardingDetector */}
          <Route
            path={routes.onboarding}
            element={
              <OnboardingDetector>
                <OnboardingPage />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.home}
            element={
              <OnboardingDetector>
                <Home />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.offices}
            element={
              <OnboardingDetector>
                <Offices />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.book}
            element={
              <OnboardingDetector>
                <Book />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.editDesk}
            element={
              <OnboardingDetector>
                <EditDesks />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.editOffice}
            element={
              <OnboardingDetector>
                <EditOfficePage />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.createOffice}
            element={
              <OnboardingDetector>
                <CreateOfficePage />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.createDesk}
            element={
              <OnboardingDetector>
                <CreateDeskPage />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.createDeskWithOffice}
            element={
              <OnboardingDetector>
                <CreateDeskPage />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.viewOffice}
            element={
              <OnboardingDetector>
                <ViewOffice />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.notifications}
            element={
              <OnboardingDetector>
                <OfficeNotifications />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.profile}
            element={
              <OnboardingDetector>
                <Profile />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.myTeam}
            element={
              <OnboardingDetector>
                <MyTeam />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.people}
            element={
              <OnboardingDetector>
                <People />
              </OnboardingDetector>
            }
          />
          <Route
            path={routes.admin}
            element={
              <OnboardingDetector>
                <AdminPage />
              </OnboardingDetector>
            }
          />
        </Routes>
      </LocalizationProvider>
    </PWAUpdater>
  )
}

export default App
